import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  GitMerge,
  User,
  Mail,
  Phone,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SortableTableHeader from '../components/SortableTableHeader';
import BalanceTotalsBar from '../components/transactions/BalanceTotalsBar';
import SalesTransactionSummaryCards, {
  type SalesSummaryCardStatus,
} from '../components/SalesTransactionSummaryCards';
import CustomerMergeModal from '../components/CustomerMergeModal';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../utils/customerApi';
import { getSalesTransactions } from '../utils/salesTransactionApi';
import {
  encodeCustomerSort,
  loadCustomersTablePreferences,
  parseCustomerSort,
  saveCustomersTablePreferences,
  type SortDir,
} from '../utils/customersTablePreferences';
import { ApiValidationError } from '../types/auth';
import type { Customer, CreateCustomerData, UpdateCustomerData, CustomerType, PaymentTerms } from '../types/customer';
import type { SalesTransactionSummary } from '../types/salesTransaction';
import type { PaginationMeta } from '../types/journalEntry';
import { formatCurrency } from '../utils/formatCurrency';
import { toast, toastApiError } from '../utils/toast';
import { computeCustomerTotals, customerRowKey } from '../utils/customerSelection';

const CUSTOMER_TYPES: CustomerType[] = ['RETAIL', 'WHOLESALE', 'CORPORATE', 'GOVERNMENT'];
const PAYMENT_TERMS: PaymentTerms[] = ['IMMEDIATE', 'NET_30', 'NET_60', 'NET_90'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const SEARCH_DEBOUNCE_MS = 400;

type ServerSortKey =
  | 'display_name'
  | 'email'
  | 'customer_type'
  | 'balance'
  | 'credit_balance'
  | 'active';

const SERVER_SORT_KEYS = new Set<ServerSortKey>([
  'display_name',
  'email',
  'customer_type',
  'balance',
  'credit_balance',
  'active',
]);

const isServerSortable = (column: string): column is ServerSortKey =>
  SERVER_SORT_KEYS.has(column as ServerSortKey);

const defaultSortDir = (key: ServerSortKey): SortDir =>
  key === 'balance' || key === 'credit_balance' ? 'desc' : 'asc';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const customerOpenBalance = (customer: Customer) => parseFloat(customer.balance);

const customerCreditBalance = (customer: Customer) => parseFloat(customer.credit_balance ?? '0');

const canEditCustomer = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteCustomer = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultEditFormData = (): UpdateCustomerData => ({
  display_name: '',
  email: '',
  phone: '',
  address: '',
  customer_type: 'RETAIL',
  payment_terms: 'IMMEDIATE',
  active: true,
});

const defaultCreateFormData = (): CreateCustomerData => ({
  display_name: '',
  email: '',
  phone: '',
  address: '',
  customer_type: 'RETAIL',
  payment_terms: 'IMMEDIATE',
  active: true,
});

const Customers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | CustomerType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [balanceFilter, setBalanceFilter] = useState<'all' | 'with_balance' | 'zero_balance'>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => loadCustomersTablePreferences().perPage);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [mergeTarget, setMergeTarget] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<UpdateCustomerData>(defaultEditFormData());
  const [createFormData, setCreateFormData] = useState<CreateCustomerData>(defaultCreateFormData());
  const [editError, setEditError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [salesSummary, setSalesSummary] = useState<SalesTransactionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<ServerSortKey>(() => {
    const parsed = parseCustomerSort(loadCustomersTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.field;
    }
    return 'display_name';
  });
  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const parsed = parseCustomerSort(loadCustomersTablePreferences().sort);
    if (parsed && isServerSortable(parsed.field)) {
      return parsed.direction;
    }
    return 'asc';
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const showActionsColumn = canEditCustomer() || canDeleteCustomer();

  useEffect(() => {
    const q = searchParams.get('q')?.trim();
    if (!q) return;
    setSearchInput(q);
    setSearchQuery(q);
  }, []);

  useEffect(() => {
    saveCustomersTablePreferences({
      sort: encodeCustomerSort(sortKey, sortDir),
      perPage,
    });
  }, [sortKey, sortDir, perPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchSalesSummary = async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const result = await getSalesTransactions({
        type: 'invoice',
        paid_days: 30,
        per_page: 1,
        page: 1,
      });
      setSalesSummary(result.summary);
    } catch (err) {
      setSummaryError(err instanceof Error ? err.message : 'Failed to fetch sales summary');
      setSalesSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesSummary();
  }, []);

  const fetchCustomers = async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCustomers({
        search: searchQuery || undefined,
        customer_type: typeFilter === 'all' ? undefined : typeFilter,
        active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        has_balance:
          balanceFilter === 'all'
            ? undefined
            : balanceFilter === 'with_balance',
        sort: encodeCustomerSort(sortKey, sortDir),
        per_page: perPage,
        page: pageNum,
      });
      setCustomers(result.customers);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch customers');
      setCustomers([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter, statusFilter, balanceFilter, perPage, sortKey, sortDir]);

  const openCreateModal = () => {
    setCreateFormData(defaultCreateFormData());
    setCreateError(null);
    setCreateFieldErrors({});
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateFormData(defaultCreateFormData());
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateFieldErrors({});

    if (!createFormData.display_name?.trim()) {
      setCreateFieldErrors({ display_name: ['Display name is required'] });
      return;
    }

    try {
      setCreating(true);
      await createCustomer({
        display_name: createFormData.display_name.trim(),
        email: createFormData.email?.trim() || null,
        phone: createFormData.phone?.trim() || null,
        address: createFormData.address?.trim() || null,
        customer_type: createFormData.customer_type,
        payment_terms: createFormData.payment_terms,
        active: createFormData.active,
      });
      closeCreateModal();
      await Promise.all([fetchCustomers(1), fetchSalesSummary()]);
      toast.success('Customer created successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setCreateFieldErrors(err.errors);
        setCreateError(err.message);
      } else {
        toastApiError(err, 'Failed to create customer');
      }
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      display_name: customer.display_name,
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      address: customer.address ?? '',
      customer_type: customer.customer_type,
      payment_terms: customer.payment_terms,
      active: customer.active,
    });
    setEditError(null);
    setFieldErrors({});
    setShowEditModal(true);
  };

  const handleEditCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer) return;

    setEditError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      await updateCustomer(editingCustomer.id, {
        display_name: formData.display_name?.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
        customer_type: formData.customer_type,
        payment_terms: formData.payment_terms,
        active: formData.active,
      });
      setShowEditModal(false);
      setEditingCustomer(null);
      await Promise.all([fetchCustomers(page), fetchSalesSummary()]);
      toast.success('Customer updated successfully!');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setEditError(err.message);
      } else {
        toastApiError(err, 'Failed to update customer');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCustomer = async (customerId: number) => {
    if (!canDeleteCustomer()) {
      toast.error('Only administrators can delete customers.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await deleteCustomer(customerId);
      await Promise.all([fetchCustomers(page), fetchSalesSummary()]);
      toast.success('Customer deleted successfully!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete customer');
    }
  };

  const handleCustomerMerged = async () => {
    setMergeTarget(null);
    await Promise.all([fetchCustomers(page), fetchSalesSummary()]);
    toast.success('Customers merged successfully!');
  };

  const hasActiveFilters =
    searchQuery || typeFilter !== 'all' || statusFilter !== 'all' || balanceFilter !== 'all';

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setTypeFilter('all');
    setStatusFilter('all');
    setBalanceFilter('all');
    setPage(1);
  };

  const handleCustomerSelect = (customer: Customer) => {
    navigate(`/sales-transactions?customer=${customer.id}`);
  };

  const handleSummaryCardClick = (status: SalesSummaryCardStatus) => {
    if (status === 'open' || status === 'overdue') {
      setBalanceFilter('with_balance');
      setPage(1);
      return;
    }

    navigate('/sales', { state: { statusFilter: status } });
  };

  const handleSort = (column: ServerSortKey) => {
    if (sortKey === column) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column);
      setSortDir(defaultSortDir(column));
    }
  };

  const fieldError = (field: string) => fieldErrors[field]?.[0];
  const createFieldError = (field: string) => createFieldErrors[field]?.[0];

  const pageTotals = useMemo(() => computeCustomerTotals(customers), [customers]);

  const visibleRowKeys = useMemo(
    () => customers.map(customerRowKey),
    [customers]
  );

  const selectedRows = useMemo(
    () => customers.filter((c) => selectedKeys.has(customerRowKey(c))),
    [customers, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeCustomerTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [searchQuery, typeFilter, statusFilter, balanceFilter, page, perPage]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : pageTotals;
  const totalsBarPageSuffix =
    meta && meta.total > pageTotals.count ? ' on this page' : '';
  const showTotalsBar = customers.length > 0;

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden w-full">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 lg:p-6 relative">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Customers</h1>
                <p className="mt-1 text-sm text-gray-500">Accounts receivable customer list</p>
              </div>
              {canEditCustomer() && (
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Customer
                </button>
              )}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Sales summary</p>
            {summaryError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-red-800 flex-1">{summaryError}</p>
                <button type="button" onClick={fetchSalesSummary} className="text-red-700 underline shrink-0">
                  Retry
                </button>
              </div>
            )}
            <SalesTransactionSummaryCards
              summary={salesSummary}
              loading={summaryLoading}
              countLabel="invoice"
              onCardClick={handleSummaryCardClick}
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchCustomers(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="lg:col-span-2">
                  <label htmlFor="customer-search" className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="customer-search"
                      type="text"
                      placeholder="Name, email, or phone..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-slate-800 focus:border-slate-600"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Type
                  </label>
                  <select
                    id="typeFilter"
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value as 'all' | CustomerType);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Types</option>
                    {CUSTOMER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {formatLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="balanceFilter" className="block text-sm font-medium text-gray-700 mb-1">
                    Balance
                  </label>
                  <select
                    id="balanceFilter"
                    value={balanceFilter}
                    onChange={(e) => {
                      setBalanceFilter(e.target.value as 'all' | 'with_balance' | 'zero_balance');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All Balances</option>
                    <option value="with_balance">With Balance</option>
                    <option value="zero_balance">Zero Balance</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="perPage" className="block text-sm font-medium text-gray-700 mb-1">
                    Per Page
                  </label>
                  <select
                    id="perPage"
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <p className="text-sm text-gray-500">
                {meta?.total ?? 0} {meta?.total === 1 ? 'customer' : 'customers'}
              </p>
              {hasActiveFilters && (
                <>
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Filters applied</span>
                  <button type="button" onClick={clearFilters} className="text-sm text-slate-700 underline">
                    Clear filters
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-full border-separate border-spacing-0">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <SortableTableHeader label="Customer" column="display_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Contact" column="email" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Type / Terms" column="customer_type" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableTableHeader label="Open balance" column="balance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Credit balance" column="credit_balance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortableTableHeader label="Status" column="active" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                    {showActionsColumn && (
                      <th className="px-4 py-3 text-right text-sm font-bold text-gray-900 bg-gray-50 uppercase tracking-wide sticky top-0 z-20 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {customers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showActionsColumn ? 8 : 7}
                        className="px-6 py-12 text-center"
                      >
                        <User className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No customers found</p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={clearFilters}
                            className="mt-4 text-sm text-slate-700 underline"
                          >
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer) => {
                      const rowKey = customerRowKey(customer);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                      <tr
                        key={rowKey}
                        className={`hover:bg-gray-50 cursor-pointer${
                          isCheckboxSelected ? ' bg-slate-50/60' : ''
                        }`}
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isCheckboxSelected}
                            onChange={(e) => toggleRow(rowKey, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-slate-800"
                            aria-label={`Select ${customer.display_name}`}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-slate-700" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{customer.display_name}</div>
                              {customer.address && (
                                <div className="text-xs text-gray-500 truncate max-w-xs">{customer.address}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-1 text-gray-900">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            {customer.email || '—'}
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                            <Phone className="h-3.5 w-3.5 text-gray-400" />
                            {customer.phone || '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="text-gray-900">{formatLabel(customer.customer_type)}</div>
                          <div className="text-xs text-gray-500">{formatLabel(customer.payment_terms)}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                          <span
                            className={
                              customerOpenBalance(customer) > 0
                                ? 'font-medium text-red-600'
                                : 'text-gray-900'
                            }
                          >
                            {formatCurrency(customerOpenBalance(customer))}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                          <span
                            className={
                              customerCreditBalance(customer) > 0
                                ? 'font-medium text-amber-700'
                                : 'text-gray-900'
                            }
                          >
                            {formatCurrency(customerCreditBalance(customer))}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${customer.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}
                          >
                            {customer.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {(canEditCustomer() || canDeleteCustomer()) && (
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              {canEditCustomer() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(customer);
                                  }}
                                  className="text-slate-600 hover:text-slate-900"
                                  title="Edit customer"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canEditCustomer() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMergeTarget(customer);
                                  }}
                                  className="text-slate-600 hover:text-slate-900"
                                  title="Merge customer"
                                >
                                  <GitMerge className="h-4 w-4" />
                                </button>
                              )}
                              {canDeleteCustomer() && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCustomer(customer.id);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  title="Delete customer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-sm text-gray-500">
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchCustomers(meta.current_page - 1)}
                  disabled={meta.current_page <= 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => fetchCustomers(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <BalanceTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              primaryLabel="Open balance"
              primaryAmount={totalsBarData.openBalance}
              secondaryLabel="Credit balance"
              secondaryAmount={totalsBarData.creditBalance}
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold">New Customer</h3>
                <p className="text-sm text-gray-500">Create a customer in your company</p>
              </div>
              <button type="button" onClick={closeCreateModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCustomer} className="p-6 space-y-4">
              {createError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{createError}</span>
                </div>
              )}
              <div>
                <label htmlFor="create-display_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  id="create-display_name"
                  type="text"
                  required
                  maxLength={200}
                  value={createFormData.display_name}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('display_name') ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="John Doe Corp"
                />
                {createFieldError('display_name') && (
                  <p className="mt-1 text-sm text-red-600">{createFieldError('display_name')}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="create-email"
                    type="email"
                    maxLength={150}
                    value={createFormData.email ?? ''}
                    onChange={(e) => setCreateFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('email') ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="john@example.com"
                  />
                  {createFieldError('email') && (
                    <p className="mt-1 text-sm text-red-600">{createFieldError('email')}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="create-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    id="create-phone"
                    type="tel"
                    maxLength={30}
                    value={createFormData.phone ?? ''}
                    onChange={(e) => setCreateFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 text-sm ${createFieldError('phone') ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="+94771234567"
                  />
                  {createFieldError('phone') && (
                    <p className="mt-1 text-sm text-red-600">{createFieldError('phone')}</p>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="create-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  id="create-address"
                  rows={2}
                  value={createFormData.address ?? ''}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="create-customer_type" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Type
                  </label>
                  <select
                    id="create-customer_type"
                    value={createFormData.customer_type}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({ ...prev, customer_type: e.target.value as CustomerType }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {CUSTOMER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {formatLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="create-payment_terms" className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    id="create-payment_terms"
                    value={createFormData.payment_terms}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({ ...prev, payment_terms: e.target.value as PaymentTerms }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {PAYMENT_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {formatLabel(term)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  id="create-active"
                  type="checkbox"
                  checked={createFormData.active ?? true}
                  onChange={(e) => setCreateFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="create-active" className="ml-2 text-sm text-gray-700">
                  Active customer
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                >
                  {creating ? 'Creating...' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-gray-800/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Edit Customer</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleEditCustomer} className="p-6 space-y-4">
              {editError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{editError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={formData.display_name ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, display_name: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${fieldError('display_name') ? 'border-red-300' : 'border-gray-300'}`}
                />
                {fieldError('display_name') && <p className="mt-1 text-sm text-red-600">{fieldError('display_name')}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  rows={2}
                  value={formData.address ?? ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Type</label>
                  <select
                    value={formData.customer_type}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, customer_type: e.target.value as CustomerType }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {CUSTOMER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {formatLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, payment_terms: e.target.value as PaymentTerms }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {PAYMENT_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {formatLabel(term)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  id="edit-active"
                  type="checkbox"
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="edit-active" className="ml-2 text-sm text-gray-700">
                  Active customer
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm disabled:opacity-60"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mergeTarget && (
        <CustomerMergeModal
          duplicate={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onMerged={() => void handleCustomerMerged()}
        />
      )}

    </div>
  );
};

export default Customers;
